import React, { useEffect, useRef, useState } from 'react';
import {
  Empty,
  SearchBar,
  DatePicker,
  Button,
  Toast,
} from 'antd-mobile';
import { BankcardOutline, CalendarOutline } from 'antd-mobile-icons';
import styled, { keyframes, css } from 'styled-components';
import { walletService } from '../services/api';
import type { WalletTransaction } from '../services/api/types';
import { r } from '../theme/riderTheme';
import {
  PageHeader,
  PageHeaderText,
  PageHeaderEyebrow,
  PageHeaderTitle,
} from '../theme/surfacePrimitives';

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  width: 100%;
`;

const HeaderBlock = styled.div`
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 14px;
  padding: 12px 12px 14px;
  margin-bottom: 14px;

  /* Restyle antd-mobile SearchBar to match the riderTheme. */
  .adm-search-bar {
    --background: ${({ theme }) => r(theme).surface.inputIdle};
    --border-radius: 10px;
    --placeholder-color: ${({ theme }) => r(theme).text.faint};
    --height: 38px;
  }
  .adm-search-bar-input-box {
    color: ${({ theme }) => r(theme).text.primary};
    font-family: 'Geist', sans-serif;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button.attrs({ type: 'button' as const })<{ $active?: boolean }>`
  padding: 6px 12px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;

  ${({ theme, $active }) =>
    $active
      ? css`
          background: ${r(theme).surface.raisedSelected};
          color: ${r(theme).text.primary};
          border: 1px solid ${r(theme).text.primary};
          box-shadow: 0 0 0 3px ${r(theme).glow.selectionLime};
        `
      : css`
          background: ${r(theme).surface.inputIdle};
          color: ${r(theme).text.muted};
          border: 1px solid ${r(theme).surface.edge};

          &:hover {
            background: ${r(theme).surface.sheet};
            color: ${r(theme).text.primary};
            border-color: ${r(theme).surface.edgeStrong};
          }
        `}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const StatsCard = styled.section`
  position: relative;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 14px;
  padding: 16px 16px 14px;
  margin-bottom: 14px;
  overflow: hidden;
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1);

  &::after {
    content: '';
    position: absolute;
    top: 16px;
    bottom: 16px;
    left: 0;
    width: 2px;
    background: ${({ theme }) =>
      `linear-gradient(180deg,
        transparent 0%,
        ${r(theme).accent.limeDeep} 24%,
        ${r(theme).accent.lime} 50%,
        ${r(theme).accent.limeDeep} 76%,
        transparent 100%
      )`};
    opacity: 0.6;
  }
`;

const StatsEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 14px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${r(theme).surface.edge}, transparent)`};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`;

const StatItem = styled.div`
  text-align: left;
  padding-right: 10px;

  & + & {
    border-left: 1px solid ${({ theme }) => r(theme).surface.edge};
    padding-left: 12px;
    padding-right: 0;
  }
`;

const StatValue = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 4px;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const DateSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 4px 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${r(theme).surface.edge}, transparent)`};
  }
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TransactionRow = styled.article`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  animation: ${fadeIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const TransactionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

type TxnTone = 'credit' | 'debit' | 'neutral';

const txnToneOf = (type: string): TxnTone => {
  if (type === 'topup' || type === 'refund') return 'credit';
  if (type === 'pack_purchase') return 'debit';
  return 'neutral';
};

const TypeRow = styled.div<{ $tone: TxnTone }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme, $tone }) => {
    const t = r(theme);
    if ($tone === 'credit') return t.accent.ok;
    if ($tone === 'debit') return t.text.primary;
    return t.text.muted;
  }};
  margin-bottom: 4px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme, $tone }) => {
      const t = r(theme);
      if ($tone === 'credit') return t.accent.ok;
      if ($tone === 'debit') return t.text.primary;
      return t.text.muted;
    }};
  }
`;

const TransactionDescription = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.primary};
  line-height: 1.3;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
`;

const TransactionScooter = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.muted};
  line-height: 1.25;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
`;

const TransactionTime = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const TransactionRight = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const TransactionAmount = styled.div<{ $tone: TxnTone }>`
  font-family: 'Geist', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${({ theme, $tone }) => {
    const t = r(theme);
    if ($tone === 'credit') return t.accent.ok;
    if ($tone === 'debit') return t.text.primary;
    return t.text.muted;
  }};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const EmptyStateContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const LoadingShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 12px;
`;

const LoadingSpinner = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edge};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 0.9s linear infinite;
`;

const FootLine = styled.div`
  padding: 20px;
  text-align: center;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

type TransactionFilter = 'all' | 'topup' | 'pack_purchase' | 'refund';

interface TransactionGroup {
  date: string;
  transactions: WalletTransaction[];
}

const TransactionHistoryPage: React.FC = () => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const transactionsRef = useRef<WalletTransaction[]>([]);
  const generationRef = useRef(0);
  const loadingRef = useRef(false);
  const [filter, setFilter] = useState<TransactionFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  // Picker is staged: 'from' opens the first date picker, then transitions to
  // 'to' on confirm. null = closed. The backend already accepts fromDate +
  // toDate as separate bounds, so the UI just needs to drive both.
  const [datePickerStage, setDatePickerStage] = useState<'from' | 'to' | null>(null);
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null);
  // antd-mobile's DatePicker fires onClose right after onConfirm. Without this
  // guard, advancing from "from" → "to" gets clobbered by onClose resetting
  // the stage back to null in the same batch.
  const skipNextCloseRef = useRef(false);
  const [stats, setStats] = useState({
    totalSpent: 0,
    totalLoaded: 0,
    transactionCount: 0,
  });

  useEffect(() => {
    loadTransactions(true);
    return () => { generationRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dateRange]);

  const getDateParam = (date: Date, boundary: 'start' | 'end'): string => {
    const value = new Date(date);
    if (boundary === 'start') {
      value.setHours(0, 0, 0, 0);
    } else {
      value.setHours(23, 59, 59, 999);
    }
    return value.toISOString();
  };

  const loadTransactions = async (reset: boolean = false) => {
    if (!reset && (loadingRef.current || !cursorRef.current)) return;
    if (reset) {
      generationRef.current += 1;
      cursorRef.current = null;
      transactionsRef.current = [];
      setTransactions([]);
      calculateStats([]);
      setHasMore(false);
    }
    const generation = generationRef.current;
    loadingRef.current = true;
    setLoading(true);
    setLoadError(false);

    try {
      const response = await walletService.getTransactionHistory(
        cursorRef.current,
        20,
        {
          transactionType: filter === 'all' ? undefined : filter,
          fromDate: dateRange[0] ? getDateParam(dateRange[0], 'start') : undefined,
          toDate: dateRange[1] ? getDateParam(dateRange[1], 'end') : undefined,
        },
      );
      if (generation !== generationRef.current) return;
      const merged = new Map(transactionsRef.current.map(txn => [txn.transaction_id, txn]));
      response.items.forEach(txn => merged.set(txn.transaction_id, txn));
      transactionsRef.current = [...merged.values()];
      setTransactions(transactionsRef.current);
      calculateStats(transactionsRef.current);
      cursorRef.current = response.nextCursor;
      setHasMore(Boolean(response.nextCursor));
    } catch (error) {
      if (generation !== generationRef.current) return;
      setLoadError(true);
      console.error('Error loading transactions:', error);
      Toast.show({
        content: 'Failed to load transactions',
        position: 'bottom',
      });
    } finally {
      if (generation === generationRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  const calculateStats = (txns: WalletTransaction[]) => {
    const next = txns.reduce(
      (acc, txn) => {
        const amount = Number(txn.amount_npr) || 0;
        if (txn.type === 'topup') {
          acc.totalLoaded += amount;
        } else if (txn.type === 'pack_purchase') {
          acc.totalSpent += amount;
        }
        acc.transactionCount++;
        return acc;
      },
      { totalSpent: 0, totalLoaded: 0, transactionCount: 0 },
    );

    setStats(next);
  };

  const groupTransactionsByDate = (txns: WalletTransaction[]): TransactionGroup[] => {
    const groups: { [key: string]: WalletTransaction[] } = {};

    txns.forEach(txn => {
      const date = new Date(txn.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey: string;
      if (date.toDateString() === today.toDateString()) {
        dateKey = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = 'Yesterday';
      } else {
        dateKey = date.toLocaleDateString('en-NP', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(txn);
    });

    return Object.entries(groups).map(([date, transactionsInGroup]) => ({
      date,
      transactions: transactionsInGroup,
    }));
  };

  const formatAmount = (transaction: WalletTransaction): string => {
    const tone = txnToneOf(transaction.type);
    const prefix = tone === 'debit' ? '−' : tone === 'credit' ? '+' : '';
    return `${prefix}${walletService.formatCurrency(transaction.amount_npr || 0)}`;
  };

  const formatTypeLabel = (type: string): string =>
    type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-NP', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredTransactions = transactions.filter(txn => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (txn.notes || '').toLowerCase().includes(query)
        || (txn.scooter_label || '').toLowerCase().includes(query)
        || (txn.scooter_vin || '').toLowerCase().includes(query)
      );
    }
    return true;
  });

  // ─── Date range helpers ───────────────────────────────────────────────────
  const rangeActive = Boolean(dateRange[0] && dateRange[1]);

  const formatChipDate = (d: Date) =>
    d.toLocaleDateString('en-NP', { month: 'short', day: 'numeric' });

  const rangeLabel = rangeActive
    ? `${formatChipDate(dateRange[0]!)} – ${formatChipDate(dateRange[1]!)}`
    : 'Date range';

  const onRangeChipClick = () => {
    if (rangeActive) {
      // Clear the active range.
      setDateRange([null, null]);
      return;
    }
    setPendingFrom(null);
    setDatePickerStage('from');
  };

  const onDatePickerClose = () => {
    if (skipNextCloseRef.current) {
      skipNextCloseRef.current = false;
      return;
    }
    setDatePickerStage(null);
    setPendingFrom(null);
  };

  const onDatePickerConfirm = (val: Date) => {
    if (!val) {
      setDatePickerStage(null);
      setPendingFrom(null);
      return;
    }
    if (datePickerStage === 'from') {
      // Suppress the onClose that antd-mobile fires immediately after this
      // confirm — we want to keep the picker open and re-title it for "to".
      skipNextCloseRef.current = true;
      setPendingFrom(val);
      setDatePickerStage('to');
      return;
    }
    if (datePickerStage === 'to') {
      const fromCandidate = pendingFrom ?? val;
      // Auto-swap if the user picked an end that's before the start so the
      // dispatched range is always ascending.
      const [lo, hi] =
        fromCandidate.getTime() <= val.getTime()
          ? [fromCandidate, val]
          : [val, fromCandidate];
      setDateRange([lo, hi]);
      setPendingFrom(null);
      setDatePickerStage(null);
    }
  };

  const transactionGroups = groupTransactionsByDate(filteredTransactions);

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderText>
          <PageHeaderEyebrow>Wallet · Activity</PageHeaderEyebrow>
          <PageHeaderTitle>Transaction history</PageHeaderTitle>
        </PageHeaderText>
      </PageHeader>

      <HeaderBlock>
        <SearchBar
          placeholder="Search loaded transactions"
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <FilterRow>
          <FilterChip $active={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </FilterChip>
          <FilterChip
            $active={filter === 'topup'}
            onClick={() => setFilter('topup')}
          >
            Top-ups
          </FilterChip>
          <FilterChip
            $active={filter === 'pack_purchase'}
            onClick={() => setFilter('pack_purchase')}
          >
            Purchases
          </FilterChip>
          <FilterChip
            $active={rangeActive}
            onClick={onRangeChipClick}
            aria-label={
              rangeActive ? `${rangeLabel}. Tap to clear date range.` : 'Pick a date range'
            }
          >
            <CalendarOutline style={{ fontSize: '12px' }} />
            {rangeLabel}
            {rangeActive && <span aria-hidden>×</span>}
          </FilterChip>
        </FilterRow>
      </HeaderBlock>

      {stats.transactionCount > 0 && (
        <StatsCard>
          <StatsEyebrow>Loaded activity · Summary</StatsEyebrow>
          <StatsGrid>
            <StatItem>
              <StatValue>{walletService.formatCurrency(stats.totalLoaded)}</StatValue>
              <StatLabel>Loaded</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{walletService.formatCurrency(stats.totalSpent)}</StatValue>
              <StatLabel>Spent</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{stats.transactionCount}</StatValue>
              <StatLabel>Transactions</StatLabel>
            </StatItem>
          </StatsGrid>
        </StatsCard>
      )}

      {loading && transactions.length === 0 ? (
        <LoadingShell>
          <LoadingSpinner />
        </LoadingShell>
      ) : transactionGroups.length === 0 ? (
        <EmptyStateContainer>
          <Empty
            description={loadError
              ? 'Transaction history could not be loaded. Retry to continue.'
              : hasMore
                ? 'No matching transactions in the history loaded so far. Load older to continue searching.'
                : 'No transactions found'}
            image={<BankcardOutline style={{ fontSize: '48px', color: 'rgba(18, 18, 21, 0.30)' }} />}
          />
        </EmptyStateContainer>
      ) : (
        <>
          {transactionGroups.map(group => (
            <div key={group.date}>
              <DateSection>{group.date}</DateSection>
              <TransactionList>
                {group.transactions.map(transaction => {
                  const tone = txnToneOf(transaction.type);
                  const scooterLabel = transaction.scooter_label || transaction.scooter_vin;
                  return (
                    <TransactionRow key={transaction.transaction_id}>
                      <TransactionInfo>
                        <TypeRow $tone={tone}>
                          {formatTypeLabel(transaction.type)}
                        </TypeRow>
                        <TransactionDescription>
                          {transaction.notes}
                        </TransactionDescription>
                        {transaction.type === 'pack_purchase' && scooterLabel && (
                          <TransactionScooter>
                            Scooter · {scooterLabel}
                          </TransactionScooter>
                        )}
                        <TransactionTime>
                          {formatTime(transaction.timestamp)}
                        </TransactionTime>
                      </TransactionInfo>
                      <TransactionRight>
                        <TransactionAmount $tone={tone}>
                          {formatAmount(transaction)}
                        </TransactionAmount>
                        <StatusPill>Completed</StatusPill>
                      </TransactionRight>
                    </TransactionRow>
                  );
                })}
              </TransactionList>
            </div>
          ))}

        </>
      )}

      <FootLine>
        {loadError ? (
          <Button onClick={() => void loadTransactions(!cursorRef.current)} disabled={loading}>
            Retry transactions
          </Button>
        ) : hasMore ? (
          <Button onClick={() => void loadTransactions(false)} loading={loading}>
            Load older
          </Button>
        ) : transactions.length > 0 && !loading ? 'All matching transactions loaded' : null}
      </FootLine>

      <DatePicker
        visible={datePickerStage !== null}
        onClose={onDatePickerClose}
        onConfirm={onDatePickerConfirm}
        title={datePickerStage === 'to' ? 'Pick end date' : 'Pick start date'}
        min={datePickerStage === 'to' && pendingFrom ? pendingFrom : undefined}
        confirmText={datePickerStage === 'from' ? 'Next' : 'Apply'}
      />
    </PageContainer>
  );
};

export default TransactionHistoryPage;
